// SPDX-License-Identifier: AGPL-3.0-or-later

import {Attachment} from '@app/features/channel/components/embeds/attachments/Attachment';
import {AttachmentMosaic} from '@app/features/channel/components/embeds/attachments/AttachmentMosaic';
import {
	buildGalleryAttachments,
	isValidMedia,
} from '@app/features/channel/components/embeds/channel_embed/ChannelEmbedShared';
import {InlineThumbnailRenderer} from '@app/features/channel/components/embeds/channel_embed/EmbedMediaRenderer';
import {withFreshEmbedMediaUrls} from '@app/features/channel/components/embeds/channel_embed/FreshEmbedUrls';
import styles from '@app/features/channel/components/MessageComponentsV2.module.css';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import type {EmbedMedia, MessageEmbed} from '@fluxer/schema/src/domains/message/EmbedSchemas';
import type {
	MessageActionRow,
	MessageComponentResponse,
} from '@fluxer/schema/src/domains/message/MessageComponentSchemas';
import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {CSSProperties, ReactNode} from 'react';

type SectionComponent = Extract<MessageComponentResponse, {type: 9}>;
type ThumbnailComponent = Extract<SectionComponent['accessory'], {type: 11}>;
type AccessoryButton = Extract<SectionComponent['accessory'], {type: 2}>;
type MediaGalleryComponent = Extract<MessageComponentResponse, {type: 12}>;
type FileComponent = Extract<MessageComponentResponse, {type: 13}>;
type V2Media = ThumbnailComponent['media'];
type V2Node = MessageComponentResponse | ThumbnailComponent;

interface V2Props {
	message: Message;
	isPreview: boolean;
	renderRow: (row: MessageActionRow, key: number) => ReactNode;
	renderAccessory: (component: AccessoryButton) => ReactNode;
}

const ComponentTypes = {
	ACTION_ROW: 1,
	SECTION: 9,
	TEXT_DISPLAY: 10,
	THUMBNAIL: 11,
	MEDIA_GALLERY: 12,
	FILE: 13,
	SEPARATOR: 14,
	CONTAINER: 17,
} as const;

const ATTACHMENT_URL_PREFIX = 'attachment://';

type ResolvedMedia = {kind: 'attachment'; attachment: MessageAttachment} | {kind: 'sized'; media: Required<EmbedMedia>};

function attachmentFor(message: Message, url: string): MessageAttachment | undefined {
	return message.componentAttachmentByUrl?.get(url);
}

// As with embeds, only sized media proxy media is drawn, through the embed NSFW gate its flags open; an external URL
// the server did not proxy or size is never fetched.
function resolveMedia(message: Message, media: V2Media, description?: string | null): ResolvedMedia | null {
	const attachment = attachmentFor(message, media.url);
	if (attachment) return {kind: 'attachment', attachment};
	if (!media.proxy_url || media.url.startsWith(ATTACHMENT_URL_PREFIX)) return null;
	const candidate = {
		...withFreshEmbedMediaUrls(media as EmbedMedia),
		description: description ?? undefined,
	} as Partial<EmbedMedia>;
	return isValidMedia(candidate) ? {kind: 'sized', media: candidate} : null;
}

function attachmentMedia(attachment: MessageAttachment, description?: string | null): Required<EmbedMedia> | null {
	const candidate = {
		url: attachment.url,
		proxy_url: attachment.proxy_url,
		width: attachment.width,
		height: attachment.height,
		content_type: attachment.content_type,
		placeholder: attachment.placeholder,
		flags: attachment.flags,
		description: description ?? attachment.description ?? undefined,
	} as Partial<EmbedMedia>;
	return isValidMedia(candidate) ? candidate : null;
}

const Thumbnail = observer(function Thumbnail({
	node,
	message,
	isPreview,
}: {
	node: ThumbnailComponent;
	message: Message;
	isPreview: boolean;
}) {
	const resolved = resolveMedia(message, node.media, node.description);
	if (!resolved) return null;
	const media = resolved.kind === 'sized' ? resolved.media : attachmentMedia(resolved.attachment, node.description);
	if (!media) return null;
	const embed = {id: `v2-thumbnail-${message.id}`, type: 'rich', thumbnail: media} as MessageEmbed;
	return (
		<div className={styles.thumbnail} data-flx="channel.message-components-v2.thumbnail.thumbnail">
			<InlineThumbnailRenderer
				embed={embed}
				message={message}
				isPreview={isPreview}
				data-flx="channel.message-components-v2.thumbnail.inline-thumbnail-renderer"
			/>
		</div>
	);
});

const MediaGallery = observer(function MediaGallery({
	node,
	message,
	isPreview,
	index,
}: {
	node: MediaGalleryComponent;
	message: Message;
	isPreview: boolean;
	index: number;
}) {
	const items = node.items.flatMap((item) => {
		const resolved = resolveMedia(message, item.media, item.description);
		return resolved ? [{resolved, description: item.description}] : [];
	});
	if (items.length === 0) return null;
	const embed = {id: `v2-gallery-${message.id}-${index}`, type: 'rich'} as MessageEmbed;
	const attachments = items.map(({resolved}, itemIndex) =>
		resolved.kind === 'attachment'
			? resolved.attachment
			: {
					...buildGalleryAttachments([resolved.media], embed)[0],
					id: `${embed.id}-${itemIndex}`,
				},
	);
	return (
		<div className={styles.gallery} data-flx="channel.message-components-v2.media-gallery.gallery">
			<AttachmentMosaic
				attachments={attachments}
				message={message}
				hideExpiryFootnote={true}
				isPreview={isPreview}
				data-flx="channel.message-components-v2.media-gallery.attachment-mosaic"
			/>
		</div>
	);
});

function FileAttachment({node, message, isPreview}: {node: FileComponent; message: Message; isPreview: boolean}) {
	const attachment = attachmentFor(message, node.file.url);
	if (!attachment) return null;
	return (
		<Attachment
			attachment={attachment}
			message={message}
			isPreview={isPreview}
			renderInMosaic={false}
			data-flx="channel.message-components-v2.file-attachment.attachment"
		/>
	);
}

function renderNode(node: V2Node, index: number, props: V2Props): ReactNode {
	const {message, isPreview} = props;
	switch (node.type) {
		case ComponentTypes.ACTION_ROW:
			return props.renderRow(node, index);
		case ComponentTypes.TEXT_DISPLAY:
			return (
				<div
					key={index}
					className={clsx(markupStyles.markup, styles.textDisplay)}
					data-flx="channel.message-components-v2.render-node.text-display"
				>
					<SafeMarkdown
						content={node.content}
						options={{
							context: MarkdownContext.STANDARD_WITHOUT_JUMBO,
							messageId: message.id,
							channelId: message.channelId,
						}}
						data-flx="channel.message-components-v2.render-node.safe-markdown"
					/>
				</div>
			);
		case ComponentTypes.SECTION: {
			const accessory =
				node.accessory.type === ComponentTypes.THUMBNAIL ? (
					<Thumbnail
						node={node.accessory}
						message={message}
						isPreview={isPreview}
						data-flx="channel.message-components-v2.render-node.thumbnail"
					/>
				) : (
					<div className={styles.accessoryButton} data-flx="channel.message-components-v2.render-node.accessory-button">
						{props.renderAccessory(node.accessory)}
					</div>
				);
			return (
				<div key={index} className={styles.section} data-flx="channel.message-components-v2.render-node.section">
					<div className={styles.sectionText} data-flx="channel.message-components-v2.render-node.section-text">
						{node.components.map((child, childIndex) => renderNode(child, childIndex, props))}
					</div>
					{accessory}
				</div>
			);
		}
		case ComponentTypes.THUMBNAIL:
			if (!resolveMedia(message, node.media)) return null;
			return (
				<Thumbnail
					key={index}
					node={node}
					message={message}
					isPreview={isPreview}
					data-flx="channel.message-components-v2.render-node.thumbnail--2"
				/>
			);
		case ComponentTypes.MEDIA_GALLERY:
			if (!node.items.some((item) => resolveMedia(message, item.media))) return null;
			return (
				<MediaGallery
					key={index}
					node={node}
					message={message}
					isPreview={isPreview}
					index={index}
					data-flx="channel.message-components-v2.render-node.media-gallery"
				/>
			);
		case ComponentTypes.FILE:
			if (!attachmentFor(message, node.file.url)) return null;
			return (
				<FileAttachment
					key={index}
					node={node}
					message={message}
					isPreview={isPreview}
					data-flx="channel.message-components-v2.render-node.file-attachment"
				/>
			);
		case ComponentTypes.SEPARATOR:
			return (
				<div
					key={index}
					role="separator"
					className={clsx(
						styles.separator,
						node.divider !== false && styles.divider,
						node.spacing === 2 && styles.spacingLarge,
					)}
					data-flx="channel.message-components-v2.render-node.separator"
				/>
			);
		case ComponentTypes.CONTAINER: {
			const accent = node.accent_color != null ? `#${node.accent_color.toString(16).padStart(6, '0')}` : undefined;
			return (
				<div
					key={index}
					className={clsx(styles.container, accent && styles.accented)}
					style={accent ? ({'--v2-accent': accent} as CSSProperties) : undefined}
					data-flx="channel.message-components-v2.render-node.container"
				>
					{node.components.map((child, childIndex) => renderNode(child, childIndex, props))}
				</div>
			);
		}
		default:
			return null;
	}
}

export function MessageComponentsV2(props: V2Props) {
	const nodes = props.message.components.map((node, index) => renderNode(node, index, props));
	if (nodes.every((node) => node == null)) return null;
	return (
		<div className={styles.root} data-flx="channel.message-components-v2.root">
			{nodes}
		</div>
	);
}
